import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { registerCallId } from '../../apis/realtime/api';
import { createRealtimeWebRtcConnection } from './webrtc-connection';
import { createAudioSignalMonitor } from './audio-monitor';
import {
  attachStreamToAudioElement,
  bindPlaybackListeners,
  playAudioElement,
} from './audio-element';
import {
  AUDIO_MONITOR_DEFAULTS,
  OPENAI_ERROR_CORRELATION_WINDOW_MS,
  OPENAI_ERROR_RING_SIZE,
  REALTIME_CONNECTION_STATUS,
  REALTIME_RECONNECT_DELAYS_MS,
  REALTIME_STATS_RING,
  TONE_ONSET_LOOKBACK_MS,
} from './constants';
import { logRealtimeEvent } from './event-log';
import {
  OPENAI_ERROR_ACTION,
  classifyOpenAiError,
  shouldReconnectOnRepeatedServerErrors,
} from './openai-errors';
import {
  describeAudioElement,
  describeMediaStream,
  getToneDetectorMode,
  readInboundStatsDelta,
  realtimeDebugLog,
  registerRealtimeDebugProviders,
  resetRealtimeDebugSnapshot,
  startInboundStatsRing,
  stopInboundStatsRing,
  updateRealtimeDebugSnapshot,
} from './debug';
import { markRealtimePlaybackStarted, stopCurrentRealtimePlayback } from '../realtime-voice';
// Temporary developer-only observability (network connectivity investigation,
// Step 3). A no-op unless FEATURES.NETWORK_DIAGNOSTICS — see the store for the full rationale.
import { useNetworkDiagnostics } from '../../zustand/network-diagnostics-store';

const initialState = {
  connectionStatus: REALTIME_CONNECTION_STATUS.IDLE,
  error: null,
  sessionId: null,
  exerciseId: null,
  callId: null,
  // Whether the agent is *audibly* talking — from the RMS monitor, not from the
  // peer connection state.
  openAiVoiceAudible: false,
  latestAssistantText: '',
  // Which event actually produced latestAssistantText — 'text' (response.output_text,
  // no audio was ever generated for this reply) vs 'audio_transcript' (the caption
  // for a spoken reply). The two render identically in the UI, so without this a
  // text-only response and a genuine playback failure are indistinguishable from
  // the outside.
  latestAssistantTextSource: null,
  latestAssistantLatency: null,
};

const buildTargetKey = (sessionId, exerciseId) => `${sessionId}::${exerciseId}`;

/**
 * Non-reactive companion state. DOM nodes, RTCPeerConnection and AudioContext
 * must never live in reactive state — they'd trigger renders and get compared
 * by identity. Stored on the store purely so everything has one owner.
 */
const createRuntimeHelpers = set => {
  const runtime = {
    monitor: null,
    commitPlaybackLatency: null,
    cleanupAudioElementListeners: null,
    connectPromise: null,
    connectionTargetKey: null,
    activeConnection: null,
    audioElement: null,
    remoteStream: null,
    setAudibility: null,
    // Reconnect bookkeeping for an unexpected disconnect — see scheduleReconnect
    // inside connect(). Not reset by cleanupRuntime (a reconnect attempt calls
    // cleanupRuntime on itself), only on success or a genuinely new target.
    reconnectAttempts: 0,
    reconnectTimer: null,
    // Bumped by every pausePlayback/resumePlayback call. A resume that resolves
    // its awaited monitor start after a later call (pause or another resume)
    // already changed the intended state checks this and bails instead of
    // calling play() on top of a since-changed decision.
    playbackGeneration: 0,

    // --- Diagnostics -------------------------------------------------------
    // All of the below is measurement only; nothing reads it to make a
    // decision. It exists to answer two open questions: is the buzz server-sent
    // or locally generated, and does it have anything to do with the backend's
    // response.create race.
    //
    // Set from output_audio_buffer.started/stopped. If a tone is still
    // measurable after OpenAI has said it stopped sending audio, the tone is
    // provably not in what OpenAI sent — one boolean settles it.
    lastAudioBufferStartedAt: null,
    lastAudioBufferStoppedAt: null,
    audioBufferActive: false,
    // OpenAI `error` events: a tally per code, the most recent one, and a small
    // ring of timestamps for "how many landed near this anomaly".
    openAiErrorCounts: {},
    lastOpenAiError: null,
    openAiErrorTimestamps: [],
    // Codes already reported to Sentry this session. A backend race fires
    // dozens per session; without this the project drowns and the signal is
    // lost in its own volume.
    sentryCapturedCodes: new Set(),
    toneAnomalyCount: 0,
    // Set by connect() so handleMessage can reach the backoff — scheduleReconnect
    // closes over connect()'s arguments and cannot be hoisted out of it.
    scheduleReconnect: null,
  };

  runtime.setAudibility = audible => {
    set({ openAiVoiceAudible: audible });
    updateRealtimeDebugSnapshot({ audioSignalPresent: audible });
    // The only line in the feed that means "the user can hear it right now" —
    // everything else only proves data moved.
    logRealtimeEvent({
      source: 'audio',
      type: audible ? 'audible.start' : 'audible.stop',
      summary: audible ? 'sound is coming out of the element' : 'silence',
    });
  };

  runtime.commitPlaybackLatency = source => {
    const latencyPayload = markRealtimePlaybackStarted(source);
    // Null means no measurement was open — playback started for a reason we
    // never armed a timer for, which is not an error.
    if (latencyPayload) {
      set({ latestAssistantLatency: latencyPayload });
    }
    return latencyPayload;
  };

  runtime.monitor = createAudioSignalMonitor({
    onAudibilityChange: audible => runtime.setAudibility(audible),
    onPlaybackDetected: source => runtime.commitPlaybackLatency(source),
    // handleAudioAnomaly is a hoisted function declaration further down this
    // module — safe to reference here since it's only ever called later, long
    // after the whole module (including useRealtimeConnectionStore, which its
    // body reads via getState()) has finished initializing.
    onAnomalyDetected: snapshot => handleAudioAnomaly(snapshot),
  });

  return runtime;
};

/**
 * One Sentry capture per distinct key per session. Both call sites here fire on
 * conditions that repeat — a stuck tone re-detects every cooldown, a backend
 * race errors dozens of times a session — and an un-deduplicated capture makes
 * the issue unreadable at exactly the volume that proves it matters.
 */
function captureOnce(runtime, key, message, extra) {
  if (runtime.sentryCapturedCodes.has(key)) {
    return;
  }
  runtime.sentryCapturedCodes.add(key);

  try {
    Sentry.captureMessage(message, { level: 'warning', extra });
  } catch (_error) {
    // Telemetry must never break playback.
  }
}

/**
 * Reacts to the "unexpected sustained tone" issue (Jira: reported by patients
 * mid-reply, on assorted devices/browsers/OSs, unrelated to server/test load).
 *
 * Currently instrumentation only. The detector that feeds this
 * (checkToneAnomaly in audio-monitor.js) was rewritten because its predecessor
 * could not fire at all — its concentration gate was computed on dB-scaled
 * bytes and was unreachable against any real noise floor — so no field data has
 * ever validated a threshold here. Cutting a patient's audio on an unvalidated
 * detector is worse than the buzz, hence observe-by-default; flip with
 * `localStorage.setItem('realtime-tone-mode', 'heal')` (see getToneDetectorMode).
 *
 * The payload is built to answer the question the heal cannot: whether the tone
 * is in what OpenAI sent, in packet-loss concealment, or in the local render
 * path. Everything is keyed to `onsetAt` rather than now — detection lands
 * ~1.2s after the tone starts, so a point read here misses the event entirely.
 */
async function handleAudioAnomaly(snapshot) {
  const reason = 'exercise:audio-anomaly-autoheal';
  const store = useRealtimeConnectionStore.getState();
  const { runtime } = store;
  const now = performance.now();

  runtime.toneAnomalyCount += 1;

  const detail = {
    peakBin: snapshot.peakBin,
    peakFrequencyHz: Math.round(snapshot.peakFrequencyHz),
    prominenceDb: Number(snapshot.prominenceDb.toFixed(1)),
    meanProminenceDb: Number(snapshot.meanProminenceDb.toFixed(1)),
    heldForMs: snapshot.heldForMs,
    rms: Number(snapshot.rms.toFixed(4)),
    rmsMin: Number(snapshot.rmsMin.toFixed(4)),
    rmsMax: Number(snapshot.rmsMax.toFixed(4)),
    // Elevated means the main thread was starved when the tone appeared, which
    // would point the fix at the pose pipeline rather than at audio.
    tickLagMs: Math.round(snapshot.tickLagMs),

    // The decisive one. `false` here means OpenAI had already told us it
    // stopped sending audio while we were still measuring a stable tone —
    // proof the tone is generated locally.
    audioBufferActive: runtime.audioBufferActive,
    msSinceAudioBufferStopped:
      runtime.lastAudioBufferStoppedAt === null
        ? null
        : Math.round(snapshot.onsetAt - runtime.lastAudioBufferStoppedAt),

    // Transport behaviour over the seconds leading up to onset, as a delta.
    inboundDelta: readInboundStatsDelta(snapshot.onsetAt - TONE_ONSET_LOOKBACK_MS),

    // Correlation probe against the backend's response.create race. Expected to
    // be 0 — if it consistently is not, the two are related after all.
    openAiErrorsInWindow: runtime.openAiErrorTimestamps.filter(
      entry => now - entry.at <= OPENAI_ERROR_CORRELATION_WINDOW_MS
    ).length,
  };

  const mode = getToneDetectorMode();

  updateRealtimeDebugSnapshot({
    lastTonePeakHz: detail.peakFrequencyHz,
    lastToneProminenceDb: detail.prominenceDb,
    toneAnomalyCount: runtime.toneAnomalyCount,
    tickLagMs: detail.tickLagMs,
  });

  logRealtimeEvent({
    source: 'audio',
    type: mode === 'heal' ? 'anomaly.autoheal' : 'anomaly.observe',
    summary: `possible tone glitch (peak ${detail.peakFrequencyHz}Hz, prominence ${detail.prominenceDb}dB, held ${detail.heldForMs}ms) — ${mode}`,
    detail,
  });

  captureOnce(runtime, 'tone-anomaly', 'realtime audio anomaly detected', detail);

  if (mode !== 'heal') {
    return;
  }

  store.pausePlayback(reason);
  window.setTimeout(() => {
    useRealtimeConnectionStore.getState().resumePlayback(reason);
  }, AUDIO_MONITOR_DEFAULTS.toneHealPauseMs);
}

function applyRemoteStream(runtime, stream, hydrateAudioElement) {
  runtime.remoteStream = stream;
  updateRealtimeDebugSnapshot({
    hasRemoteStream: Boolean(stream),
    stream: describeMediaStream(stream),
  });

  hydrateAudioElement();
  runtime.monitor.start(stream).catch(() => {});
}

function cleanupRuntime(runtime) {
  if (runtime.activeConnection) {
    // Named in the log because a superseded call is exactly what leaves the
    // backend holding a call id OpenAI has already forgotten.
    realtimeDebugLog('disposing superseded call', runtime.activeConnection.getCallId?.() ?? null);
  }

  runtime.connectPromise = null;
  runtime.connectionTargetKey = null;

  // A pending reconnect for the connection being torn down is now moot —
  // either it just succeeded (about to open a fresh one below) or the caller
  // is leaving the target entirely (disconnect()). Attempts count is left
  // alone: a reconnect calls cleanupRuntime on itself, and clearing it there
  // would defeat the backoff (every retry would reset to attempt 0).
  if (runtime.reconnectTimer) {
    clearTimeout(runtime.reconnectTimer);
    runtime.reconnectTimer = null;
  }

  // The ring is bound to one peer connection's getStats; a new connection
  // starts a new one from onConnected.
  stopInboundStatsRing();
  runtime.audioBufferActive = false;

  runtime.monitor?.stop();
  runtime.setAudibility?.(false);

  runtime.cleanupAudioElementListeners?.();
  runtime.cleanupAudioElementListeners = null;

  runtime.activeConnection?.dispose();
  runtime.activeConnection = null;

  stopCurrentRealtimePlayback({ current: runtime.audioElement });
  runtime.remoteStream = null;
}

export const useRealtimeConnectionStore = create((set, get) => {
  const runtime = createRuntimeHelpers(set);

  // Feeds `window.__realtimeAudioReport()` the two things only the store owns.
  registerRealtimeDebugProviders({
    getAudioElement: () => runtime.audioElement,
    getAudioLevel: () => runtime.monitor?.getLastRms?.() ?? null,
  });

  /**
   * The <audio> ref and the remote stream arrive in no guaranteed order, so this
   * runs from both paths and bails until it has both. Without it you get a
   * connected call and silence.
   */
  const hydrateAudioElement = () => {
    if (!runtime.audioElement || !runtime.remoteStream) {
      // Named explicitly: "connected but silent" is almost always one of these
      // two halves never showing up, and the symptom is identical either way.
      realtimeDebugLog('audio not hydrated yet', {
        hasAudioElement: Boolean(runtime.audioElement),
        hasRemoteStream: Boolean(runtime.remoteStream),
      });
      return;
    }

    if (!runtime.cleanupAudioElementListeners) {
      runtime.cleanupAudioElementListeners = bindPlaybackListeners(
        runtime.audioElement,
        runtime.commitPlaybackLatency
      );
    }

    attachStreamToAudioElement(runtime.audioElement, runtime.remoteStream);
    realtimeDebugLog('audio element hydrated', describeAudioElement(runtime.audioElement));
    logRealtimeEvent({
      source: 'audio',
      type: 'element.hydrated',
      summary: 'stream attached to <audio>',
      detail: describeAudioElement(runtime.audioElement),
    });

    // Autoplay can be refused; that surfaces as silence, not an exception worth
    // propagating. Connecting after a user gesture is what actually avoids it.
    // Reported rather than swallowed: a NotAllowedError here is the single most
    // common reason for a healthy connection with nothing audible.
    playAudioElement(runtime.audioElement, runtime.commitPlaybackLatency)
      .then(() =>
        logRealtimeEvent({ source: 'audio', type: 'play.ok', summary: 'element is playing' })
      )
      .catch(error => {
        console.error('[realtime] audio playback was refused', error?.name ?? error);
        updateRealtimeDebugSnapshot({ playbackBlocked: error?.name ?? 'PlaybackRefused' });
        logRealtimeEvent({
          source: 'error',
          type: 'play.refused',
          summary: error?.name ?? 'PlaybackRefused',
        });
      });
  };

  // Which delta/done event produced the text, mapped to the modality it
  // proves: 'text' means OpenAI generated no audio at all for this reply —
  // response.output_text is the text-only track, distinct from the transcript
  // that accompanies a spoken one. Both render identically in the UI, so this
  // tag is the only way to tell "this reply was never going to have sound"
  // apart from "sound was generated but didn't play".
  const TEXT_SOURCE_BY_EVENT_TYPE = {
    'response.output_text.delta': 'text',
    'response.output_text.done': 'text',
    'response.output_audio_transcript.delta': 'audio_transcript',
    'response.output_audio_transcript.done': 'audio_transcript',
    'response.audio_transcript.delta': 'audio_transcript',
    'response.audio_transcript.done': 'audio_transcript',
  };

  /**
   * Records an OpenAI `error` event and decides — almost always "do nothing" —
   * what it means for the connection. See openai-errors.js for why the default
   * is inaction and why classification keys on `type` rather than `code`.
   */
  const handleOpenAiError = event => {
    const { action, code, type, message } = classifyOpenAiError(event);
    const at = performance.now();

    runtime.openAiErrorCounts[code] = (runtime.openAiErrorCounts[code] ?? 0) + 1;
    runtime.lastOpenAiError = { code, type, message, eventId: event?.event_id ?? null, at };
    runtime.openAiErrorTimestamps.push({ at, type, code });
    if (runtime.openAiErrorTimestamps.length > OPENAI_ERROR_RING_SIZE) {
      runtime.openAiErrorTimestamps.splice(
        0,
        runtime.openAiErrorTimestamps.length - OPENAI_ERROR_RING_SIZE
      );
    }

    updateRealtimeDebugSnapshot({
      lastOpenAiErrorCode: code,
      openAiErrorCounts: { ...runtime.openAiErrorCounts },
    });

    // Breadcrumb every one so the trail is there when something else is
    // captured; capture at most one per code, per session.
    try {
      Sentry.addBreadcrumb({
        category: 'realtime.openai-error',
        level: 'warning',
        message: `${code}: ${message}`,
        data: { type, count: runtime.openAiErrorCounts[code] },
      });
    } catch (_error) {
      // Telemetry must never break playback.
    }
    captureOnce(runtime, `openai-error:${code}`, `realtime OpenAI error: ${code}`, {
      type,
      message,
      count: runtime.openAiErrorCounts[code],
    });

    const repeatedServerErrors = shouldReconnectOnRepeatedServerErrors(
      runtime.openAiErrorTimestamps,
      runtime.lastAudioBufferStartedAt,
      OPENAI_ERROR_CORRELATION_WINDOW_MS,
      at
    );

    if (action !== OPENAI_ERROR_ACTION.RECONNECT && !repeatedServerErrors) {
      return;
    }

    logRealtimeEvent({
      source: 'system',
      type: 'reconnect.onOpenAiError',
      summary: repeatedServerErrors
        ? 'repeated server_error with no audio — reconnecting'
        : `${code} — reconnecting`,
    });
    runtime.scheduleReconnect?.();
  };

  const handleMessage = event => {
    if (event.type === 'error') {
      handleOpenAiError(event);
      return;
    }

    // Lifecycle of OpenAI's own output buffer. Recorded here rather than in
    // webrtc-connection.js because the store owns the state; the connection
    // only forwards the event.
    if (event.type === 'output_audio_buffer.started') {
      runtime.lastAudioBufferStartedAt = performance.now();
      runtime.audioBufferActive = true;
      return;
    }

    if (
      event.type === 'output_audio_buffer.stopped' ||
      event.type === 'output_audio_buffer.cleared'
    ) {
      runtime.lastAudioBufferStoppedAt = performance.now();
      runtime.audioBufferActive = false;
      return;
    }

    if (event.type === 'response.created') {
      set({
        latestAssistantText: '',
        latestAssistantTextSource: null,
        latestAssistantLatency: null,
      });
      return;
    }

    const textSource = TEXT_SOURCE_BY_EVENT_TYPE[event.type];

    // A spoken reply carries its words as transcript deltas, not text deltas —
    // without these the panel shows an empty string while the agent is talking.
    const isTextDelta =
      event.type === 'response.output_text.delta' ||
      event.type === 'response.output_audio_transcript.delta' ||
      event.type === 'response.audio_transcript.delta';

    if (isTextDelta && typeof event.delta === 'string') {
      set(state => ({
        latestAssistantText: state.latestAssistantText + event.delta,
        latestAssistantTextSource: textSource,
      }));
      return;
    }

    const isTextDone =
      event.type === 'response.output_text.done' ||
      event.type === 'response.output_audio_transcript.done' ||
      event.type === 'response.audio_transcript.done';

    if (isTextDone) {
      const finalText = typeof event.text === 'string' ? event.text : event.transcript;
      if (typeof finalText === 'string') {
        set({ latestAssistantText: finalText, latestAssistantTextSource: textSource });
      }
      return;
    }

    if (event.type === 'response.done') {
      const finalText = (event.response?.output ?? [])
        .flatMap(item => item?.content ?? [])
        .filter(part => part?.type === 'output_text')
        .map(part => part.text ?? '')
        .join('');

      if (finalText) {
        set({ latestAssistantText: finalText, latestAssistantTextSource: 'text' });
      }
    }
  };

  return {
    ...initialState,
    runtime,

    attachAudioElement: element => {
      runtime.audioElement = element ?? null;
      updateRealtimeDebugSnapshot({ hasAudioElement: Boolean(element) });
      hydrateAudioElement();
    },

    clearLatestAssistantLatency: () => set({ latestAssistantLatency: null }),

    async connect({ sessionId, exerciseId }) {
      const targetKey = buildTargetKey(sessionId, exerciseId);
      const currentState = get();
      const isSameTarget = runtime.connectionTargetKey === targetKey;

      // A genuinely new target starts its own attempt count — carrying over a
      // previous target's exhausted retries would make the very first failure
      // on this one look like the last of a backoff and give up immediately.
      if (!isSameTarget) {
        runtime.reconnectAttempts = 0;
      }

      // Already connected to this exact target: don't burn a new token, just
      // make sure the (possibly remounted) <audio> element is wired up again.
      if (isSameTarget && currentState.connectionStatus === REALTIME_CONNECTION_STATUS.OPEN) {
        hydrateAudioElement();
        runtime.setAudibility(false);
        return { callId: currentState.callId };
      }

      // A connect for this target is already in flight — hand back the same
      // promise rather than racing a second call.
      if (
        runtime.connectPromise &&
        isSameTarget &&
        currentState.connectionStatus === REALTIME_CONNECTION_STATUS.CONNECTING
      ) {
        return runtime.connectPromise;
      }

      // The peer connection can still be `connected` while the store's status
      // has drifted to error — a page remount re-runs this effect after a
      // transient failure was recorded. Minting a second call there is what
      // leaves the backend attaching to a dead id, so reuse the live one and
      // just make sure the backend's cache points at it.
      const liveConnection = runtime.activeConnection;
      const livePeerState = liveConnection?.getPeerConnection?.()?.connectionState ?? null;

      if (isSameTarget && liveConnection && livePeerState === 'connected') {
        const liveCallId = liveConnection.getCallId?.() ?? null;
        realtimeDebugLog('reusing live call instead of minting a new one', liveCallId);

        set({
          connectionStatus: REALTIME_CONNECTION_STATUS.OPEN,
          callId: liveCallId,
          error: null,
        });
        hydrateAudioElement();

        if (liveCallId) {
          registerCallId(liveCallId, sessionId, exerciseId).catch(error => {
            console.error(
              '[realtime] re-register of the live call failed',
              error?.message ?? error
            );
          });
        }

        return { callId: liveCallId };
      }

      cleanupRuntime(runtime);

      set({
        ...initialState,
        connectionStatus: REALTIME_CONNECTION_STATUS.CONNECTING,
        sessionId,
        exerciseId,
      });
      updateRealtimeDebugSnapshot({ status: 'connecting', sessionId, exerciseId, error: null });

      // An unexpected drop (ICE failure, OpenAI closing the call) used to leave
      // connectionStatus stuck at ERROR forever — nothing in the app ever
      // re-called connect() on its own, since sessionId/exerciseId don't change
      // mid-exercise. This re-mints a token and redoes the whole handshake from
      // scratch (a Realtime call can't be resumed), with backoff so a genuinely
      // dead session doesn't hammer the token endpoint.
      const scheduleReconnect = () => {
        if (runtime.reconnectTimer) {
          return;
        }

        const attemptIndex = runtime.reconnectAttempts;
        if (attemptIndex >= REALTIME_RECONNECT_DELAYS_MS.length) {
          logRealtimeEvent({
            source: 'system',
            type: 'reconnect.gaveUp',
            summary: `stopped retrying after ${attemptIndex} attempt(s)`,
          });
          updateRealtimeDebugSnapshot({ reconnecting: false });
          return;
        }

        const delayMs = REALTIME_RECONNECT_DELAYS_MS[attemptIndex];
        runtime.reconnectAttempts += 1;
        updateRealtimeDebugSnapshot({
          reconnecting: true,
          reconnectAttempt: runtime.reconnectAttempts,
        });
        useNetworkDiagnostics.getState().record('webrtc', 'reconnect_scheduled', delayMs, 'ms', {
          userVisibleImpact: 'Reconnection occurred',
        });
        logRealtimeEvent({
          source: 'system',
          type: 'reconnect.scheduled',
          summary: `attempt ${runtime.reconnectAttempts} in ${delayMs}ms`,
        });

        runtime.reconnectTimer = setTimeout(() => {
          runtime.reconnectTimer = null;
          get()
            .connect({ sessionId, exerciseId })
            .catch(() => {});
        }, delayMs);
      };

      // handleMessage lives outside connect() and so cannot close over this.
      runtime.scheduleReconnect = scheduleReconnect;

      const connection = createRealtimeWebRtcConnection({
        sessionId,
        exerciseId,
        // Every callback re-checks ownership: a superseded connection resolving
        // late must not write over the current one's state.
        onRemoteStream: stream => {
          if (runtime.activeConnection !== connection) {
            return;
          }
          applyRemoteStream(runtime, stream, hydrateAudioElement);
        },
        onMessage: event => {
          if (runtime.activeConnection !== connection) {
            return;
          }
          handleMessage(event);
        },
        onStateChange: () => {},
        onConnected: ({ callId, setupDurationMs }) => {
          if (runtime.activeConnection !== connection) {
            return;
          }
          // Started here rather than at connect() time: getStats() has nothing
          // to report until the peer connection is actually up.
          startInboundStatsRing(
            () => connection.getPeerConnection?.() ?? null,
            REALTIME_STATS_RING
          );
          if (typeof setupDurationMs === 'number') {
            useNetworkDiagnostics
              .getState()
              .record('webrtc', 'connection_setup_time', setupDurationMs, 'ms');
          }
          runtime.reconnectAttempts = 0;
          updateRealtimeDebugSnapshot({ reconnecting: false, reconnectAttempt: 0 });
          set({ connectionStatus: REALTIME_CONNECTION_STATUS.OPEN, callId, error: null });
          updateRealtimeDebugSnapshot({ status: 'open', callId, error: null });
        },
        onDisconnected: error => {
          if (runtime.activeConnection !== connection) {
            return;
          }
          set({
            connectionStatus: REALTIME_CONNECTION_STATUS.ERROR,
            error: error?.message ?? 'Realtime connection lost',
          });
          updateRealtimeDebugSnapshot({ status: 'error', error: error?.message ?? null });
          useNetworkDiagnostics.getState().record('webrtc', 'connection_error', 1, 'count', {
            userVisibleImpact: 'Voice did not connect',
          });
          scheduleReconnect();
        },
      });

      runtime.activeConnection = connection;
      runtime.connectionTargetKey = targetKey;

      const connectPromise = connection
        .connect()
        .then(result => {
          if (runtime.activeConnection !== connection) {
            return result;
          }
          hydrateAudioElement();
          return result;
        })
        .catch(error => {
          if (runtime.activeConnection === connection) {
            set({
              connectionStatus: REALTIME_CONNECTION_STATUS.ERROR,
              error: error?.message ?? 'Realtime connection failed',
            });
            updateRealtimeDebugSnapshot({ status: 'error', error: error?.message ?? null });
            scheduleReconnect();
          }
          throw error;
        })
        .finally(() => {
          if (runtime.connectPromise === connectPromise) {
            runtime.connectPromise = null;
          }
        });

      runtime.connectPromise = connectPromise;
      return connectPromise;
    },

    /**
     * Retries playback from inside a user gesture.
     *
     * The call is opened on the camera page, which can be reached by a direct
     * link — no click has happened in the document yet, so the autoplay policy
     * refuses the `play()` that hydration attempts and the whole call is silent
     * with a perfectly healthy connection. A `play()` issued from a real click
     * is the one thing that beats that policy, so the Continue button calls
     * this on its way to the next page.
     *
     * Safe to call at any time: a no-op when there is nothing to play, and it
     * resolves rather than throws when playback is refused again.
     */
    async resumePlayback(reason = 'unknown') {
      const element = runtime.audioElement;
      // Bumped before the await below, and checked again after it: a pause (or
      // a newer resume) that lands while this call is still waiting on the
      // AudioContext must win — otherwise this call's play() fires on top of a
      // decision that has since changed, which is exactly the "stage flips to
      // POST_PERFORMANCE, pause fires, then the still-pending resume from
      // IN_PERFORMANCE re-plays it anyway" race.
      const myGeneration = ++runtime.playbackGeneration;

      // The AudioContext is suspended under the same autoplay policy, and a
      // suspended one silently reports no signal. Rebuilding the monitor graph
      // (rather than only resuming a possibly-stale one) is also what makes it
      // agree with the element: pausePlayback tears the graph down, so audible
      // detection cannot report signal while <audio> is deliberately paused.
      if (runtime.remoteStream) {
        await runtime.monitor?.start?.(runtime.remoteStream);
      } else {
        await runtime.monitor?.resume?.();
      }

      if (myGeneration !== runtime.playbackGeneration) {
        return false;
      }

      if (!element?.srcObject) {
        return false;
      }

      try {
        await playAudioElement(element, runtime.commitPlaybackLatency);
        updateRealtimeDebugSnapshot({ playbackBlocked: null });
        logRealtimeEvent({
          source: 'audio',
          type: 'play.resumed',
          summary: `play() retried (${reason})`,
        });
        return true;
      } catch (error) {
        updateRealtimeDebugSnapshot({ playbackBlocked: error?.name ?? 'PlaybackRefused' });
        logRealtimeEvent({
          source: 'error',
          type: 'play.refused',
          summary: `${reason}: ${error?.name ?? 'PlaybackRefused'}`,
        });
        return false;
      }
    },

    pausePlayback(reason = 'unknown') {
      const element = runtime.audioElement;
      // See resumePlayback: this invalidates any resume that is still awaiting
      // its monitor start, so it cannot re-play on top of this pause once it
      // resolves.
      runtime.playbackGeneration += 1;
      runtime.monitor?.stop();

      if (!element) {
        return false;
      }

      element.pause();
      set({ openAiVoiceAudible: false });
      logRealtimeEvent({
        source: 'audio',
        type: 'play.paused',
        summary: `playback paused (${reason})`,
      });
      return true;
    },

    /**
     * Points the backend's cache back at the call we actually hold.
     *
     * The backend reads the cached id when the exercise socket hydrates. A
     * socket that reopens mid-session hydrates again, so without this the
     * backend can go on using whatever id it captured first — which is how a
     * live call ends up ignored in favour of a dead one. Cheap by design: no
     * new token, no new call, just the cache pointer.
     */
    async reRegisterCurrentCall() {
      const { callId, sessionId, exerciseId, connectionStatus } = get();

      if (!callId || connectionStatus !== REALTIME_CONNECTION_STATUS.OPEN) {
        return false;
      }

      try {
        await registerCallId(callId, sessionId, exerciseId);
        realtimeDebugLog('re-registered call id after socket reopen', callId);
        return true;
      } catch (error) {
        console.error('[realtime] re-register failed', error?.message ?? error);
        return false;
      }
    },

    disconnect() {
      cleanupRuntime(runtime);
      // Leaving the target on purpose (route change) — unlike a reconnect
      // calling cleanupRuntime on itself, there is no retry to preserve a count
      // for.
      runtime.reconnectAttempts = 0;
      set({ ...initialState });
      resetRealtimeDebugSnapshot();
    },
  };
});

/**
 * Is the user hearing the agent right now? Reads through `getState()` so it can
 * be called from outside React — specifically from inside the MediaPipe frame
 * loop, where hooks are not available and a re-render would be unacceptable.
 */
export function getIsWebRtcAudioRunning() {
  const { runtime } = useRealtimeConnectionStore.getState();
  const audioElement = runtime?.audioElement;

  if (!audioElement?.srcObject) {
    return false;
  }
  if (audioElement.paused || audioElement.ended) {
    return false;
  }

  return runtime.monitor.isSignalPresent() || !audioElement.paused;
}

export function disconnectRealtimeConnection() {
  useRealtimeConnectionStore.getState().disconnect();
}

/**
 * Called from the exercise socket's `onOpen`, which is outside React — hence
 * `getState()` rather than a hook. A no-op when there is no live call, so the
 * socket does not need to know whether the voice agent is enabled.
 */
export function reRegisterRealtimeCall() {
  return useRealtimeConnectionStore.getState().reRegisterCurrentCall();
}

/**
 * Called from a click handler, which is why it is a plain function rather than
 * a hook: the caller is a button that also navigates, and it must not have to
 * subscribe to the store to unblock audio.
 */
export function resumeRealtimePlayback(reason = 'unknown') {
  return useRealtimeConnectionStore.getState().resumePlayback(reason);
}

export function pauseRealtimePlayback(reason = 'unknown') {
  return useRealtimeConnectionStore.getState().pausePlayback(reason);
}
